import type { FollowerHistoryPoint } from "../hooks/useFollowerHistory";
import { addDays, previousRange, type DayRange } from "./dayAxis";

/** How far back a boundary looks for a snapshot: the nightly snapshot can
 *  miss a few nights, never a week. */
const BOUNDARY_DAYS = 6;

export interface FollowerWindow {
  platform: string;
  /** The count at the window's end; null without a snapshot near it. */
  count: number | null;
  /** The net change over the window, and over the window before; null when
   *  either boundary has no snapshot (a gap, DD-021). */
  change: number | null;
  previousChange: number | null;
}

/** Per platform: the count at the window's end, the net change over the
 *  window and over the window before. A boundary reads the latest snapshot
 *  on or before its day and at most 6 days before it, else null (a gap):
 *  count = at(until), change = at(until) - at(since - 1), previousChange =
 *  at(since - 1) - at(previous.since - 1). Platforms in name order. */
export const followerWindow = (
  points: ReadonlyArray<FollowerHistoryPoint>,
  span: DayRange,
): FollowerWindow[] => {
  const byPlatform = new Map<string, FollowerHistoryPoint[]>();
  points.forEach((p) => byPlatform.set(p.platform, [...(byPlatform.get(p.platform) ?? []), p]));
  const before = previousRange(span);
  return Array.from(byPlatform.keys())
    .sort()
    .map((platform) => {
      const snapshots = byPlatform.get(platform) ?? [];
      const at = (day: string): number | null => {
        const floor = addDays(day, -BOUNDARY_DAYS);
        const found = snapshots
          .filter((p) => p.date <= day && p.date >= floor)
          .reduce<FollowerHistoryPoint | null>((latest, p) => (!latest || p.date > latest.date ? p : latest), null);
        return found ? found.followerCount : null;
      };
      const end = at(span.until);
      const start = at(addDays(span.since, -1));
      const earlier = at(addDays(before.since, -1));
      return {
        platform,
        count: end,
        change: end != null && start != null ? end - start : null,
        previousChange: start != null && earlier != null ? start - earlier : null,
      };
    });
};
